/**
 * Ramp Tool - Ramp Creation
 * 
 * Creates a ramp defined by start and end points (run).
 * Features:
 * - Run based creation
 * - Slope calculation
 * - Width control
 */

import { v4 as uuidv4 } from 'uuid';
import { Element } from '../core/types';
import { BaseTool } from './BaseTool';
import { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';

export interface RampGeometryData {
    start: [number, number, number];
    end: [number, number, number];
    width: number;
    slope: number; // 1:x ratio or degrees
    baseLevelId: string;
    topLevelId: string;
}

export interface RampElement extends Element {
    category: 'Ramps';
    geometryData: RampGeometryData;
}

export class RampTool extends BaseTool {
    id = 'ramp';
    name = 'Ramp';
    cursor = 'crosshair';

    private startPoint: THREE.Vector3 | null = null;
    private currentPoint: THREE.Vector3 | null = null;

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const point = this.getIntersection(event);
        if (!point) return;

        const snapped = this.snapToGrid(point);

        if (!this.startPoint) {
            this.startPoint = snapped;
            this.currentPoint = snapped;
        } else {
            this.finishRamp(this.startPoint, snapped);
            this.startPoint = null;
            this.currentPoint = null;
            this.context.appStore.setToolPreview(null);
        }
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        const point = this.getIntersection(event);
        if (point) {
            this.currentPoint = this.snapToGrid(point);
            this.updatePreview();
        }
    }

    onPointerUp(): void { }

    private updatePreview() {
        if (!this.context) return;

        if (this.startPoint && this.currentPoint) {
            this.context.appStore.setToolPreview({
                type: 'ramp',
                start: this.startPoint,
                end: this.currentPoint,
                width: 1.0 // Default width
            });
        } else {
            this.context.appStore.setToolPreview(null);
        }
    }

    private finishRamp(start: THREE.Vector3, end: THREE.Vector3) {
        if (!this.context) return;

        // Default constraints
        const baseLevelId = 'Level 1';
        const topLevelId = 'Level 2';
        const width = 1.0;
        const slope = 12; // 1:12 slope

        const geometryData: RampGeometryData = {
            start: [start.x, start.y, start.z],
            end: [end.x, end.y, end.z],
            width,
            slope,
            baseLevelId,
            topLevelId
        };

        const ramp: RampElement = {
            id: uuidv4(),
            category: 'Ramps',
            type: 'Ramp: Generic',
            parameters: {
                'Base Level': baseLevelId,
                'Top Level': topLevelId,
                'Width': `${width.toFixed(2)} m`,
                'Slope': `1:${slope}`
            },
            constraints: [],
            geometryData
        };

        this.context.bimStore.addElement(ramp);
    }
}
